import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  NgZone,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { ModalFormHomeService } from '../modal-form-home/service/modal-form-home.service';
import { TituloComponent } from '../../../../shared/components/titulo/titulo.component';
import { NotificationsService } from './services/notifications.service';
import { NotificationComponent } from './components/notification/notification.component';
import { interval, timer } from 'rxjs';
import { LoadSpinnerComponent } from 'src/app/shared/components/load-spinner/load-spinner.component';
import { DataErrorComponent } from 'src/app/shared/components/data-error/data-error.component';
import { NoDataComponent } from 'src/app/shared/components/no-data/no-data.component';

const PANEL_REFRESH_INTERVAL_MS = 10 * 1000; // 10 segundos
const INITIAL_REFRESH_DELAY_MS = 2000; // 2 segundos

@Component({
  selector: 'app-notifications-panel',
  templateUrl: './notifications-panel.component.html',
  styleUrl: './notifications-panel.component.scss',
  imports: [
    MatIconModule,
    TituloComponent,
    NotificationComponent,
    LoadSpinnerComponent,
    DataErrorComponent,
    NoDataComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationsPanelComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly modalFormHomeService = inject(ModalFormHomeService);
  private readonly notificationsService = inject(NotificationsService);

  readonly selectedIds = signal<Set<string>>(new Set());
  readonly isDisableMarkAsRead = computed<boolean>(
    () => this.selectedIds().size === 0
  );

  // Consumir signals del servicio directamente
  readonly isLoading = this.notificationsService.isLoading;
  readonly error = this.notificationsService.error;
  readonly unreadNotifications = this.notificationsService.unreadNotifications;
  readonly readNotifications = this.notificationsService.readNotifications;

  readonly hasNoNotifications = computed(() => {
    return this.notificationsService.notifications().length === 0;
  });

  // Mostrar spinner solo en carga inicial (no durante refreshes)
  readonly isInitialLoading = computed(() => {
    return this.isLoading() && this.hasNoNotifications();
  });

  private ngZone = inject(NgZone);

  ngOnInit(): void {
    // Refresh inicial después de 2 segundos de abrir el panel
    this.ngZone.runOutsideAngular(() => {
      timer(INITIAL_REFRESH_DELAY_MS)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.notificationsService.refresh());
    });

    // Polling cada 1 minuto mientras el panel esté abierto
    this.ngZone.runOutsideAngular(() => {
      interval(PANEL_REFRESH_INTERVAL_MS)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.notificationsService.refresh());
    });
  }

  closePanel(): void {
    this.modalFormHomeService.showingModalHome.next(false);
    this.modalFormHomeService.closeDrawerHome.next(true);
  }

  toggleSelection(id: string): void {
    this.selectedIds.update(current => {
      const updated = new Set(current);
      if (updated.has(id)) {
        updated.delete(id);
      } else {
        updated.add(id);
      }
      return updated;
    });
  }

  isSelected(id: string): boolean {
    return this.selectedIds().has(id);
  }

  markSelectedAsRead(): void {
    const selectedSet = this.selectedIds();
    if (selectedSet.size === 0) return;

    this.notificationsService.markMultipleAsRead(Array.from(selectedSet));
    this.selectedIds.set(new Set());
  }

  notificationRefresh(): void {
    this.notificationsService.refresh();
  }
}
